// @ts-check
import React from 'react';
import classNames from 'classnames';

/**
 * Construct a basic renderer for basic elements by name.
 * @param {string} keyName The CS element name to create.
 * @param {keyof import('react').ReactHTML | 'fragment'} [elementName=keyName] The name of the React Element to create. @default keyName Same as key.
 * @returns {import('.').RenderOptionEntry}
 */
export function ConstructBasicMark(keyName, elementName) {
	return Object.assign(
		{},
		{
			/** @type {import('.').RenderMark} */
			[keyName]: (text) => (elementName === 'fragment' ? text : React.createElement(elementName ?? keyName, {}, text)),
		}
	);
}

/**
 * Construct a custom renderer for basic elements by name.
 * @param {string} keyName The element name to create.
 * @param {import('.').RenderElementByNode} render
 * @returns {import('.').RenderOptionEntry}
 */
export function ConstructCustomElement(keyName, render) {
	return Object.assign({}, { [keyName]: render });
}

/**
 * Construct a basic renderer for basic elements by name.
 * @param {string} keyName The element name to create.
 * @param {keyof import('react').ReactHTML | 'fragment'} [elementName=keyName] The name of the React Element to create. @default keyName Same as key.
 * @param {Record<string, string>} [childProps={}] Optional props to add the rendered element.
 * @returns {import('.').RenderOptionEntry}
 */
export function ConstructBasicElement(keyName, elementName, childProps = {}) {
	if (elementName === 'fragment') {
		return {
			[keyName]: (node, next) => next(node.children),
		};
	}

	return Object.assign(
		{},
		{
			/** @type {import('.').RenderElementByNode} */
			[keyName]: (node, next) => {
				const eleClass = classNames(node.attrs?.['class'], node.attrs?.['class-name'], childProps?.className);
				const eleProps = Object.assign({}, childProps);

				if (eleClass) {
					Object.assign(eleProps, { className: eleClass });
				}
				if (childProps?.id) {
					Object.assign(eleProps, { id: childProps.id });
				}

				return React.createElement(elementName ?? keyName, childProps, ...next(node.children));
			},
		}
	);
}

/**
 * A helper to construct an override for a certain key.
 * @param {string} keyName The element name to override.
 * @returns A fragment override for this element.
 */
export function ConstructFragmentOverride(keyName) {
	return ConstructBasicElement(keyName, 'fragment');
}
